/**
 * ClaimPage
 *
 * Landing page for a claim link. Each link carries a token bound to one
 * specific edition copy (e.g. piece #23/50) and can be redeemed exactly once.
 *
 * Route: /claim/:token
 *
 * How a creator distributes the link is up to them — an NFC chip in a physical
 * artwork, a QR code, a DM, an email. The copy here stays deliberately generic
 * about the delivery mechanism.
 *
 * Flow:
 *   - Not signed in  -> confirm the link is valid, show the piece, prompt sign-in.
 *                       Signing in happens in place (app-wide wallet picker), so
 *                       the visitor lands back here already authenticated.
 *   - Signed in      -> show the piece + an explicit Claim button. Minting is
 *                       deliberately NOT automatic: a page visit should never
 *                       irreversibly mint an NFT to whichever wallet happens to
 *                       be connected.
 *   - Claimed        -> confirmation + where to find the NFT.
 *
 * See docs/features/LAUNCH_CLAIM_TOKENS.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import {
  FeatherGift,
  FeatherCheckCircle,
  FeatherAlertCircle,
  FeatherLogIn,
  FeatherArrowRight,
} from '@subframe/core';
import { useAuthStore } from '../store/authStore';
import { useSignInModalStore } from '../store/signInModalStore';
import { getClaim, redeemClaim, type ClaimLookup } from '../api/claims';

export const ClaimPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, user } = useAuthStore();
  const openSignIn = useSignInModalStore((s) => s.open);

  const [claim, setClaim] = useState<ClaimLookup | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadClaim = useCallback(async () => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      const data = await getClaim(token);
      setClaim(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Re-fetch when auth changes: signing in reveals whether *this* viewer is the
  // one who already claimed, which the anonymous response can't say.
  useEffect(() => {
    loadClaim();
  }, [loadClaim, isAuthenticated]);

  const handleRedeem = async () => {
    if (!token) return;

    setRedeeming(true);
    setError(null);

    try {
      const result = await redeemClaim(token);
      if (result.success) {
        setRedeemed(true);
        await loadClaim();
      } else {
        setError(result.message || 'Could not claim this piece.');
      }
    } catch (err: any) {
      // ApiClient throws ApiHttpError with the backend's message flattened onto .message
      setError(err?.message || 'Could not claim this piece. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-96 w-full items-center justify-center">
          <LoadingSpinner />
        </div>
      </DefaultPageLayout>
    );
  }

  // Unknown and voided links are intentionally indistinguishable here.
  if (notFound || !claim) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center gap-4 py-24">
          <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
          <span className="text-heading-2 font-heading-2 text-default-font">
            This claim link isn't valid
          </span>
          <span className="max-w-md text-center text-body font-body text-subtext-color">
            Double-check the link you were given. If you believe this is a mistake, reach out to the
            creator.
          </span>
          <Link to="/">
            <Button variant="neutral-secondary">Go to XRP Deals</Button>
          </Link>
        </div>
      </DefaultPageLayout>
    );
  }

  const { piece, launch } = claim;
  const serialLabel =
    piece.serial != null && piece.max_supply != null
      ? `#${piece.serial} of ${piece.max_supply}`
      : piece.name || '';
  const pieceLabel = serialLabel ? `piece ${serialLabel}` : 'this piece';

  // Success: either just redeemed, or a repeat visit by the original claimer.
  const showClaimed = redeemed || claim.claimedByViewer;

  return (
    <DefaultPageLayout>
      <div className="flex w-full justify-center py-8 mobile:py-4">
        {/* Two columns on wide screens: the artwork on the left, whatever the
            visitor needs to do about it on the right. Stacks on narrow. */}
        <div className="grid w-full max-w-[1040px] grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          {/* ---------- Left: the piece ---------- */}
          <div className="flex flex-col items-center gap-4">
            {piece.image_url && (
              <img
                src={piece.image_url}
                alt={piece.edition_name || launch.name}
                className="w-full max-w-[440px] rounded-lg object-cover shadow-lg"
              />
            )}
            <div className="flex flex-col items-center gap-1 text-center">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {piece.edition_name || launch.name}
              </span>
              {serialLabel && (
                <span className="text-body-bold font-body-bold text-brand-700">{serialLabel}</span>
              )}
            </div>
          </div>

          {/* ---------- Right: the action / status ---------- */}
          <div className="flex w-full flex-col items-center">
            {/* Claimed */}
            {showClaimed && (
              <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-solid border-success-200 bg-success-50 px-6 py-8">
                <FeatherCheckCircle className="text-heading-1 font-heading-1 text-success-700" />
                <span className="text-heading-3 font-heading-3 text-success-800">
                  This piece is yours
                </span>
                <span className="max-w-md text-center text-body font-body text-success-700">
                  {redeemed
                    ? "We're minting your NFT now. In a moment you'll get a request in your wallet to accept it — approve that and the piece lands in your account."
                    : "You've already claimed this piece. If you haven't accepted the transfer yet, check your wallet for a pending offer."}
                </span>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {user?.xrpl_address && (
                    <Link to={`/wallets/${user.xrpl_address}`}>
                      <Button iconRight={<FeatherArrowRight />}>View my NFTs</Button>
                    </Link>
                  )}
                  {launch.collection_slug && (
                    <Link to={`/collections/${launch.collection_slug}`}>
                      <Button variant="neutral-secondary">View the collection</Button>
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Valid link, not signed in */}
            {!showClaimed && !isAuthenticated && claim.claimable && (
              <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-solid border-brand-200 bg-brand-50 px-6 py-8">
                <FeatherGift className="text-heading-1 font-heading-1 text-brand-700" />
                <span className="text-heading-3 font-heading-3 text-brand-900">
                  Your claim link is valid
                </span>
                <span className="max-w-md text-center text-body font-body text-brand-800">
                  This link unlocks {pieceLabel} as a free NFT — reserved for you and nobody else.
                  Sign in with your wallet to claim it.
                </span>
                <Button icon={<FeatherLogIn />} onClick={openSignIn}>
                  Sign in to claim
                </Button>
              </div>
            )}

            {/* Valid link, signed in: the claim action */}
            {!showClaimed && isAuthenticated && claim.claimable && (
              <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-solid border-brand-200 bg-brand-50 px-6 py-8">
                <FeatherGift className="text-heading-1 font-heading-1 text-brand-700" />
                <span className="text-heading-3 font-heading-3 text-brand-900">
                  Claim your free NFT
                </span>
                {/* Spell out the actual mechanic — mint, then a transfer offer the
                    collector has to approve — and name the destination wallet inline
                    so it reads as part of the sentence they're agreeing to. */}
                <span className="max-w-md text-center text-body font-body text-brand-800">
                  Claiming mints {pieceLabel} and sends a free transfer offer to{' '}
                  {user?.xrpl_address ? (
                    <span className="font-monospace-body text-brand-900">
                      {user.xrpl_address.slice(0, 8)}…{user.xrpl_address.slice(-6)}
                    </span>
                  ) : (
                    'your wallet'
                  )}
                  . Approve the offer in your wallet and the piece is yours.
                </span>

                <Button
                  size="large"
                  icon={<FeatherGift />}
                  loading={redeeming}
                  disabled={redeeming}
                  onClick={handleRedeem}
                >
                  {redeeming ? 'Claiming…' : 'Claim my NFT'}
                </Button>

                {error && (
                  <span className="text-center text-body font-body text-error-700">{error}</span>
                )}
              </div>
            )}

            {/* Link recognised but not claimable */}
            {!showClaimed && !claim.claimable && (
              <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-6 py-8">
                <FeatherAlertCircle className="text-heading-1 font-heading-1 text-subtext-color" />
                <span className="text-center text-heading-3 font-heading-3 text-default-font">
                  This piece can't be claimed right now
                </span>
                <span className="max-w-md text-center text-body font-body text-subtext-color">
                  {claim.reason || 'Please try again later.'}
                </span>
                {launch.collection_slug && (
                  <Link to={`/collections/${launch.collection_slug}`}>
                    <Button variant="neutral-secondary">View the collection</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
